import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import { useContent } from '../../../contexts/ContentContext';
import { contentAPI } from '../../../api';
import { API_URLS } from '../../../api/config';
import './ContentDetail.css';

/**
 * 콘텐츠 상세 정보 컴포넌트
 * 특정 콘텐츠의 상세 정보를 표시합니다.
 */
const ContentDetail = () => {
  // 상태 관리
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [content, setContent] = useState(null);
  const { type, id } = useParams();
  const { contentDetails, updateContent } = useContent();

  // Create unique ID for caching
  const uniqueId = `${type}_${id}`;

  // 캐시된 콘텐츠 확인
  const cachedContent = contentDetails[uniqueId];

  // 콘텐츠 데이터 로딩
  useEffect(() => {
    if (cachedContent) {
      setContent(cachedContent);
      setIsLoading(false);
      return;
    }

    const fetchContent = async () => {
      try {
        const data = await contentAPI.getContentDetail(type, id);
        setContent(data);
        updateContent([], { [uniqueId]: data });
      } catch (error) {
        console.error('콘텐츠 상세 정보 조회 실패:', error);
        setError(error.message);
      } finally {
        setIsLoading(false);
      }
    };

    fetchContent();
  }, [type, id, uniqueId, cachedContent, updateContent]);

  // 마크다운 이미지 렌더러
  const renderers = {
    img: ({ node, ...props }) => (
      <div className="markdown-image-container">
        <img
          {...props}
          loading="lazy"
          alt={props.alt || 'Content image'}
        />
      </div>
    )
  };

  // Helper to resolve image URL
  const getImageUrl = (path) => {
    if (!path) return '';
    if (path.startsWith('http') || path.startsWith('/')) {
      return path;
    }
    return `${API_URLS.S3}/${path}`;
  };

  // 로딩 및 에러 상태 처리
  if (isLoading) return <div className="loading">Loading content...</div>;
  if (error) return <div className="error">Error: {error}</div>;
  if (!content) return <div>No content found.</div>;

  return (
    <div className="content-detail">
      {/* 콘텐츠 헤더 */}
      <div className="content-header">
        {/* <h1>{content.title}</h1>
        <div className="content-meta">
          <span className="date">
            {new Date(content.updated_at || content.created_at).toLocaleDateString('en-US', {
              year: 'numeric',
              month: 'long',
              day: 'numeric'
            })}
          </span>
        </div> */}
      </div>

      {/* 메인 이미지 */}
      {content.image_path && (
        <div className="content-image">
          <img
            src={getImageUrl(content.image_path)}
            alt={content.title}
            loading="lazy"
          />
        </div>
      )}

      {/* 상세 이미지 (추가된 부분) */}
      {content.detail_image_path && (
        <div className="content-detail-image">
          <img
            src={getImageUrl(content.detail_image_path)}
            alt={`${content.title} Detail`}
            loading="lazy"
          />
        </div>
      )}

      {/* 텍스트 콘텐츠 */}
      {content.text_content && (
        <div className="content-body">
          <ReactMarkdown components={renderers}>
            {content.text_content}
          </ReactMarkdown>
        </div>
      )}

      {/* 유튜브 비디오 */}
      {content.youtube_video_id && (
        <div className="video-container">
          <iframe
            src={`https://www.youtube.com/embed/${content.youtube_video_id}`}
            title={content.title}
            frameBorder="0"
            allowFullScreen
            loading="lazy"
          />
        </div>
      )}

      {/* 외부 링크 */}
      {content.external_url && (
        <div className="external-link">
          <a href={content.external_url} target="_blank" rel="noopener noreferrer">
            Visit Original Article →
          </a>
        </div>
      )}
    </div>
  );
};

export default ContentDetail;
